/**
 * focusedEngagement.ts (frontend)
 *
 * Which engagement is "in focus" for a company in the UI.
 *
 * PRD §4.7 selection order:
 *   1. `engagementId` in the URL,
 *   2. the per-user, per-company preference
 *      (`pathkit.focusedEngagement:{userId}:{companyId}` in localStorage),
 *   3. the only engagement when there is exactly one,
 *   4. the most recently updated engagement with an active lifecycle status,
 *   5. the most recently created engagement.
 *
 * `pickFocusedEngagement` mirrors the server copy in
 * `supabase/functions/server/focusedEngagement.ts`; keep them in sync.
 */

export type FocusResolution =
  | 'url'
  | 'preference'
  | 'only'
  | 'latest-active'
  | 'latest-created'
  | 'none';

export interface FocusableEngagement {
  id?: string;
  status?: string;
  createdAt?: string | number;
  created_at?: string | number;
  updatedAt?: string | number;
  updated_at?: string | number;
  [key: string]: any;
}

const ACTIVE_STATUSES = new Set(['active', 'in-progress', 'in_progress']);

function toTime(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0;
  const t = new Date(value as any).getTime();
  return Number.isFinite(t) ? t : 0;
}

function createdTime(e: FocusableEngagement): number {
  return toTime(e.createdAt ?? e.created_at);
}

function updatedTime(e: FocusableEngagement): number {
  return toTime(e.updatedAt ?? e.updated_at) || createdTime(e);
}

export function isActiveEngagementStatus(status: unknown): boolean {
  return ACTIVE_STATUSES.has(String(status ?? '').toLowerCase());
}

/**
 * Pure picker: requested → only → latest active → latest created.
 * The `resolution` for a requested hit is reported as 'preference'; callers
 * that know the request came from the URL relabel it (see resolveFocusedEngagementId).
 */
export function pickFocusedEngagement<T extends FocusableEngagement>(
  engagements: ReadonlyArray<T> | null | undefined,
  requestedId?: string | null,
): { engagement: T | null; resolution: FocusResolution } {
  const list = (engagements ?? []).filter(Boolean) as T[];
  if (list.length === 0) return { engagement: null, resolution: 'none' };

  if (requestedId) {
    const hit = list.find((e) => e.id === requestedId);
    if (hit) return { engagement: hit, resolution: 'preference' };
  }

  if (list.length === 1) return { engagement: list[0], resolution: 'only' };

  const actives = list.filter((e) => isActiveEngagementStatus(e.status));
  if (actives.length > 0) {
    const latest = [...actives].sort((a, b) => updatedTime(b) - updatedTime(a))[0];
    return { engagement: latest, resolution: 'latest-active' };
  }

  const newest = [...list].sort((a, b) => createdTime(b) - createdTime(a))[0];
  return { engagement: newest, resolution: 'latest-created' };
}

// ---------------------------------------------------------------------------
// Persistence (per user, per company)
// ---------------------------------------------------------------------------

export function focusedEngagementStorageKey(userId: string, companyId: string): string {
  return `pathkit.focusedEngagement:${userId}:${companyId}`;
}

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readFocusedEngagementPreference(
  userId: string | null | undefined,
  companyId: string | null | undefined,
): string | null {
  if (!userId || !companyId) return null;
  const s = storage();
  if (!s) return null;
  try {
    const v = s.getItem(focusedEngagementStorageKey(userId, companyId));
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

export function writeFocusedEngagementPreference(
  userId: string | null | undefined,
  companyId: string | null | undefined,
  engagementId: string | null | undefined,
): void {
  if (!userId || !companyId) return;
  const s = storage();
  if (!s) return;
  try {
    const key = focusedEngagementStorageKey(userId, companyId);
    if (engagementId) s.setItem(key, engagementId);
    else s.removeItem(key);
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

export function clearFocusedEngagementPreference(
  userId: string | null | undefined,
  companyId: string | null | undefined,
): void {
  writeFocusedEngagementPreference(userId, companyId, null);
}

// ---------------------------------------------------------------------------
// URL
// ---------------------------------------------------------------------------

export function readEngagementIdFromUrl(search?: string): string | null {
  try {
    const src = typeof search === 'string' ? search : (typeof window !== 'undefined' ? window.location.search : '');
    const v = new URLSearchParams(src).get('engagementId');
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Full resolution
// ---------------------------------------------------------------------------

export interface ResolveFocusOptions {
  userId?: string | null;
  companyId?: string | null;
  /** Override for tests; defaults to window.location.search. */
  search?: string;
}

/**
 * The hint to send to the server BEFORE the engagement list is known:
 * URL first, then the stored preference. Null when neither exists.
 */
export function preferredEngagementIdHint(opts: ResolveFocusOptions): string | null {
  return readEngagementIdFromUrl(opts.search) ?? readFocusedEngagementPreference(opts.userId, opts.companyId);
}

/**
 * Resolve the focused engagement from a known list. Applies the full PRD order
 * and clears a stale stored preference that no longer matches any engagement.
 */
export function resolveFocusedEngagement<T extends FocusableEngagement>(
  engagements: ReadonlyArray<T> | null | undefined,
  opts: ResolveFocusOptions,
): { engagement: T | null; resolution: FocusResolution } {
  const fromUrl = readEngagementIdFromUrl(opts.search);
  if (fromUrl) {
    const pick = pickFocusedEngagement(engagements, fromUrl);
    if (pick.resolution === 'preference') return { engagement: pick.engagement, resolution: 'url' };
  }

  const stored = readFocusedEngagementPreference(opts.userId, opts.companyId);
  if (stored) {
    const pick = pickFocusedEngagement(engagements, stored);
    if (pick.resolution === 'preference') return pick;
    // stale preference: engagement gone or belongs elsewhere
    clearFocusedEngagementPreference(opts.userId, opts.companyId);
  }

  return pickFocusedEngagement(engagements, null);
}
